import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useForm } from "react-hook-form";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

export default function TransferMachineryModal({ isOpen, toggle, machine, onSuccess }) {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [allMachines, setAllMachines] = useState([]);

  const selectedFromSite = watch("fromSiteId");

  useEffect(() => {
    if (isOpen) {
      fetchSitesAndMachines();
      if (machine) {
        setValue("fromSiteId", machine?.siteId || machine?.Site?.id || "");
        setValue("machineryId", machine?.id || "");
        setValue("description", `Relocation of ${machine?.name} (${machine?.assetNo || machine?.machineNumber || ""})`);
      }
      setValue("date", moment().format("YYYY-MM-DD"));
    }
  }, [isOpen, machine]);

  const fetchSitesAndMachines = async () => {
    try {
      const [siteRes, machRes] = await Promise.all([
        callApi(apiUrl.get_site, { method: "GET" }),
        callApi(`${apiUrl.get_machinery}?pageSize=200`, { method: "GET" }),
      ]);

      if (siteRes?.status === true) {
        const list = Array.isArray(siteRes?.data?.site)
          ? siteRes.data.site
          : Array.isArray(siteRes?.data)
          ? siteRes.data
          : [];
        setSites(list);
      }

      if (machRes?.status === true) {
        const rawList = Array.isArray(machRes?.data?.machine)
          ? machRes.data.machine
          : Array.isArray(machRes?.data)
          ? machRes.data
          : [];
        setAllMachines(rawList);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getFinancialYear = () => {
    const today = moment();
    const curYear = today.month() < 3 ? today.year() - 1 : today.year();
    return `${curYear}-${curYear + 1}`;
  };

  const onSubmit = async (data) => {
    if (data.fromSiteId === data.toSiteId) {
      toast.error("Source site and destination site cannot be the same.");
      return;
    }

    setLoading(true);
    try {
      const selectedMach = allMachines.find((m) => String(m.id) === String(data.machineryId)) || machine;

      // 1. Create Transfer Asset record in ERP
      const transferPayload = {
        fromSiteId: data.fromSiteId,
        toSiteId: data.toSiteId,
        date: data.date,
        description: data.description || `Transfer of ${selectedMach?.name || "Machinery"}`,
        financialYear: getFinancialYear(),
        item: [
          {
            itemId: selectedMach?.id || 1,
            quantity: 1,
            remarks: `Machine Transfer: ${selectedMach?.name} (${selectedMach?.machineNumber || selectedMach?.assetNo || ""})`,
          },
        ],
      };

      const transferRes = await callApi(apiUrl.create_transfer_asset, {
        method: "POST",
        data: transferPayload,
      });

      // 2. Update machinery site location to destination site
      if (selectedMach?.id) {
        await callApi(`${apiUrl.update_machinery}?machineryId=${selectedMach.id}`, {
          method: "POST",
          data: {
            siteId: data.toSiteId,
          },
        });
      }

      if (transferRes?.status === true) {
        toast.success(`Equipment transferred successfully to new site!`);
        reset();
        toggle();
        onSuccess?.();
      } else {
        toast.info("Machinery site location updated successfully!");
        reset();
        toggle();
        onSuccess?.();
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong during transfer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          toggle={toggle}
          close={
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={toggle}
              aria-label="Close"
              style={{ filter: "invert(1) grayscale(100%) brightness(200%)", opacity: 0.9, cursor: "pointer" }}
            />
          }
          style={{ background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          🔁 Initiate Machinery Inter-Site Transfer
        </ModalHeader>
        <ModalBody>
          <div className="row g-3">
            {/* Machine Picker */}
            <div className="col-12">
              <label className="form-label">
                Select Machinery / Equipment to Transfer *
              </label>
              <select
                className="form-select"
                {...register("machineryId", { required: "Please select machinery" })}
                onChange={(e) => {
                  const m = allMachines.find((x) => String(x.id) === String(e.target.value));
                  if (m) {
                    setValue("fromSiteId", m?.siteId || m?.Site?.id || "");
                    setValue("description", `Relocation of ${m?.name} (${m?.assetNo || m?.machineNumber || ""})`);
                  }
                }}
              >
                <option value="">Select Equipment</option>
                {allMachines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {safeText(m.name, "Machine")} ({safeText(m.assetNo || m.machineNumber, "N/A")}) — Current Site: {safeText(m.Site?.name, "Unassigned")}
                  </option>
                ))}
              </select>
              {errors.machineryId && <small className="text-danger">{errors.machineryId.message}</small>}
            </div>

            {/* From Site */}
            <div className="col-md-6">
              <label className="form-label">
                Source Site (From) *
              </label>
              <select
                className="form-select"
                {...register("fromSiteId", { required: "Source site is required" })}
              >
                <option value="">Select Origin Site</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {safeText(s.name, "Site")}
                  </option>
                ))}
              </select>
              {errors.fromSiteId && <small className="text-danger">{errors.fromSiteId.message}</small>}
            </div>

            {/* To Site */}
            <div className="col-md-6">
              <label className="form-label">
                Destination Site (To) *
              </label>
              <select
                className="form-select"
                {...register("toSiteId", { required: "Destination site is required" })}
              >
                <option value="">Select Destination Site</option>
                {sites
                  .filter((s) => String(s.id) !== String(selectedFromSite))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {safeText(s.name, "Site")}
                    </option>
                  ))}
              </select>
              {errors.toSiteId && <small className="text-danger">{errors.toSiteId.message}</small>}
            </div>

            {/* Transfer Date */}
            <div className="col-md-6">
              <label className="form-label">
                Transfer Date *
              </label>
              <input
                type="date"
                className="form-control"
                {...register("date", { required: "Transfer date is required" })}
              />
            </div>

            {/* Waybill / Transporter info */}
            <div className="col-md-6">
              <label className="form-label">
                Transporter / Vehicle Reg No (Optional)
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Trailer RJ14-GC-9988"
                {...register("transporter")}
              />
            </div>

            {/* Remarks */}
            <div className="col-12">
              <label className="form-label">
                Transfer Purpose & Waybill Remarks
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Reason for machinery transfer, operator details, gate pass reference..."
                {...register("description")}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading} style={{ background: "#2563eb", borderColor: "#2563eb" }}>
            {loading ? "Processing Transfer..." : "Confirm & Transfer Machine"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
